import React, { useState, useEffect } from 'react';
import { 
  Coins, Search, Send, User, RefreshCw, CheckCircle, 
  XCircle, AlertCircle, History, Sparkles, TrendingUp,
  ShieldCheck, ArrowUpRight, ArrowDownLeft, Clock, Award, 
  Building2, Radio, Check, ChevronRight, HelpCircle,
  Copy, DollarSign, Wallet, ArrowDownCircle, ExternalLink, X, Settings, ArrowRight,
  MessageSquare, ShieldAlert, UploadCloud, FileText, Zap, CreditCard, PlusCircle, CheckCircle2
} from 'lucide-react';
import { sellerAPI } from '../utils/api';

const SellerDashboard = () => {
  // Navigation Sections: 'withdrawals' | 'deposit' | 'rates' | 'ledger'
  const [activeSection, setActiveSection] = useState('withdrawals');

  // Stats & Profile
  const [stats, setStats] = useState({
    current_balance: 0,
    total_sold: 0,
    total_transfers: 0,
    today_sold: 0,
    today_transfers: 0,
    seller_level: 'BRONZE',
    commission_percentage: 10
  });
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Rate Settings State
  const [rateData, setRateData] = useState({
    my_coins_per_rupee: 20,
    max_coins_per_rupee: 20,
    rate_display: '20 Coins / ₹1',
    is_at_max: true
  });
  const [rateInput, setRateInput] = useState('20');
  const [savingRate, setSavingRate] = useState(false);
  const [rateFeedback, setRateFeedback] = useState({ message: '', type: '' });

  // Withdrawal Requests State
  const [withdrawals, setWithdrawals] = useState([]);
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(false);
  const [withdrawalFilter, setWithdrawalFilter] = useState('pending'); // 'all', 'pending', 'paid'
  const [payingWithdrawal, setPayingWithdrawal] = useState(null);
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [copiedKey, setCopiedKey] = useState(null);
  const [declinePromptId, setDeclinePromptId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  // Seller Chat Modal State
  const [chatUser, setChatUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);

  // Transfer Target Mode: 'agency' | 'host'
  const [transferMode, setTransferMode] = useState('agency');

  // Recipient Search State
  const [recipientQuery, setRecipientQuery] = useState('');
  const [searchingRecipient, setSearchingRecipient] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  // Transfer Coins State
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSuccessMsg, setTransferSuccessMsg] = useState('');
  const [transferErrorMsg, setTransferErrorMsg] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Transactions History State
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [txFilter, setTxFilter] = useState('ALL'); // 'ALL' | 'AGENCY' | 'HOST'
  const [searchTerm, setSearchTerm] = useState('');

  // Seller Fake Payment Dispute State
  const [disputeTx, setDisputeTx] = useState(null);
  const [disputeUserId, setDisputeUserId] = useState('');
  const [disputeCoins, setDisputeCoins] = useState('');
  const [disputeReason, setDisputeReason] = useState('Payment screenshot was fake / edited');
  const [disputeCustomReason, setDisputeCustomReason] = useState('');
  const [disputeProofUrl, setDisputeProofUrl] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputeError, setDisputeError] = useState('');
  const [disputeSuccess, setDisputeSuccess] = useState('');
  const [sellerDisputes, setSellerDisputes] = useState([]);
  const [showDisputesList, setShowDisputesList] = useState(false);

  // Seller Self-Recharge (Razorpay) Modal State
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [coinSlabs, setCoinSlabs] = useState([]);
  const [loadingSlabs, setLoadingSlabs] = useState(false);
  const [selectedSlab, setSelectedSlab] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [rechargeSuccessMsg, setRechargeSuccessMsg] = useState('');
  const [rechargeErrorMsg, setRechargeErrorMsg] = useState('');
  const [razorpayKey, setRazorpayKey] = useState('rzp_live_T6xtopTqfS4nyl');

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const openRechargeModal = async () => {
    setShowRechargeModal(true);
    setRechargeErrorMsg('');
    setRechargeSuccessMsg('');
    setSelectedSlab(null);
    setLoadingSlabs(true);
    try {
      const res = await sellerAPI.getCoinSlabs();
      if (res.data?.success && res.data?.data) {
        const slabs = res.data.data.slabs || [];
        setCoinSlabs(slabs);
        if (res.data.data.razorpay_key_id) {
          setRazorpayKey(res.data.data.razorpay_key_id);
        }
        if (slabs.length > 0) {
          setSelectedSlab(slabs[0]);
        }
      }
    } catch (err) {
      setRechargeErrorMsg(err.response?.data?.message || "Failed to load wholesale coin packages");
    } finally {
      setLoadingSlabs(false);
    }
  };

  const handlePaySlab = async (slabToPay) => {
    const targetSlab = slabToPay || selectedSlab;
    if (!targetSlab) {
      setRechargeErrorMsg("Please select a coin package to recharge");
      return;
    }

    setRechargeErrorMsg('');
    setRechargeSuccessMsg('');
    setProcessingPayment(true);

    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      setProcessingPayment(false);
      setRechargeErrorMsg("Unable to load Razorpay checkout SDK. Please check your internet connection.");
      return;
    }

    const priceInr = Number(targetSlab.price_inr);
    const coinsAmount = Number(targetSlab.coin_amount);
    const amountInPaise = Math.round(priceInr * 100);

    const options = {
      key: razorpayKey || 'rzp_live_T6xtopTqfS4nyl',
      amount: amountInPaise,
      currency: 'INR',
      name: 'XOXO Coin Seller Portal',
      description: `Wholesale Recharge: ${coinsAmount.toLocaleString('en-IN')} Coins`,
      image: '/favicon.ico',
      prefill: {
        name: seller?.name || stats?.name || '',
        email: seller?.email || '',
        contact: seller?.phone || ''
      },
      theme: {
        color: '#f59e0b'
      },
      handler: async function (response) {
        try {
          const verifyRes = await sellerAPI.verifyRechargePayment({
            slab_id: targetSlab.id,
            coins_amount: coinsAmount,
            amount_inr: priceInr,
            razorpay_payment_id: response.razorpay_payment_id,
            payment_id: response.razorpay_payment_id
          });

          if (verifyRes.data?.success) {
            setRechargeSuccessMsg(verifyRes.data.message || `Payment verified! 🪙 ${coinsAmount.toLocaleString('en-IN')} Coins credited to your Seller Wallet.`);
            fetchDashboardData();
            fetchTransactions();
            setTimeout(() => {
              setShowRechargeModal(false);
              setRechargeSuccessMsg('');
              setSelectedSlab(null);
            }, 3000);
          }
        } catch (err) {
          console.error("Verification error:", err);
          setRechargeErrorMsg(err.response?.data?.message || err.message || "Payment completed, but failed to credit coins automatically. Please contact Admin with payment ID: " + response.razorpay_payment_id);
        } finally {
          setProcessingPayment(false);
        }
      },
      modal: {
        ondismiss: function () {
          setProcessingPayment(false);
        }
      }
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (resp) {
        setProcessingPayment(false);
        setRechargeErrorMsg(resp.error?.description || "Payment failed or cancelled.");
      });
      rzp.open();
    } catch (openErr) {
      setProcessingPayment(false);
      setRechargeErrorMsg("Failed to open Razorpay checkout: " + openErr.message);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchTransactions();
    fetchRateData();
    fetchWithdrawalRequests();
    fetchSellerDisputes();
  }, []);

  useEffect(() => {
    fetchWithdrawalRequests();
  }, [withdrawalFilter]);

  const fetchRateData = async () => {
    try {
      const res = await sellerAPI.getRate();
      if (res.data?.success && res.data?.data) {
        setRateData(res.data.data);
        setRateInput(String(res.data.data.my_coins_per_rupee || 20));
      }
    } catch (err) {
      console.error("Error fetching rate data:", err);
    }
  };

  const handleSaveRate = async () => {
    const val = parseFloat(rateInput);
    if (isNaN(val) || val <= 0) {
      setRateFeedback({ message: "Please enter a valid rate greater than 0", type: "error" });
      return;
    }
    if (val > rateData.max_coins_per_rupee) {
      setRateFeedback({ 
        message: `Rate cannot exceed Admin maximum cap of ${rateData.max_coins_per_rupee} coins per ₹1.`, 
        type: "error" 
      });
      return;
    }
    setSavingRate(true);
    setRateFeedback({ message: '', type: '' });
    try {
      const res = await sellerAPI.updateRate({ coins_per_rupee: val });
      if (res.data?.success) {
        setRateFeedback({ message: res.data.message || "Selling rate updated successfully!", type: "success" });
        fetchRateData();
      }
    } catch (err) {
      setRateFeedback({ message: err.response?.data?.message || "Failed to update selling rate", type: "error" });
    } finally {
      setSavingRate(false);
    }
  };

  const fetchWithdrawalRequests = async () => {
    setLoadingWithdrawals(true);
    try {
      const res = await sellerAPI.getWithdrawalRequests({ status: withdrawalFilter });
      if (res.data?.success) {
        setWithdrawals(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading withdrawal requests:", err);
    } finally {
      setLoadingWithdrawals(false);
    }
  };

  const handleAcceptWithdrawal = async (id) => {
    try {
      const res = await sellerAPI.acceptWithdrawalRequest(id);
      if (res.data?.success) {
        fetchWithdrawalRequests();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to accept withdrawal request");
    }
  };

  const handleConfirmPayment = async () => {
    if (!payingWithdrawal) return;
    if (!paymentRef.trim()) {
      setPaymentError("Payment UTR / Transaction Reference is required");
      return;
    }
    setPaymentSubmitting(true);
    setPaymentError('');
    setPaymentSuccess('');
    try {
      const res = await sellerAPI.payWithdrawalRequest(payingWithdrawal.id, {
        transaction_ref: paymentRef.trim(),
        notes: paymentNotes.trim()
      });
      if (res.data?.success) {
        setPaymentSuccess(res.data.message || "Payment confirmed and coins credited to your wallet!");
        setTimeout(() => {
          setPayingWithdrawal(null);
          setPaymentRef('');
          setPaymentNotes('');
          setPaymentSuccess('');
        }, 1500);
        fetchWithdrawalRequests();
        fetchDashboardData();
        fetchTransactions();
      }
    } catch (err) {
      setPaymentError(err.response?.data?.message || "Failed to confirm payment");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDeclineWithdrawal = async (id) => {
    try {
      const res = await sellerAPI.rejectWithdrawalRequest(id, { reason: declineReason.trim() });
      if (res.data?.success) {
        setDeclinePromptId(null);
        setDeclineReason('');
        fetchWithdrawalRequests();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to decline request");
    }
  };

  const openChatWithUser = async (user) => {
    setChatUser(user);
    setChatMessages([]);
    setChatLoading(true);
    try {
      const res = await sellerAPI.getChatMessages(user.user_id);
      if (res.data?.success) {
        const msgs = res.data.messages || res.data.data || [];
        setChatMessages(msgs);
      }
    } catch (err) {
      console.error("Error loading chat messages:", err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatUser || !chatInput.trim() || chatSending) return;
    const msgText = chatInput.trim();
    setChatSending(true);
    try {
      const res = await sellerAPI.sendChatMessage(chatUser.user_id, { message: msgText });
      if (res.data?.success) {
        setChatInput('');
        const newMsg = res.data.data || {
          sender_id: seller?.user_id,
          text: msgText,
          created_at: new Date().toISOString()
        };
        setChatMessages(prev => [...prev, newMsg]);
      }
    } catch (err) {
      console.error("Failed to send chat message:", err);
      alert(err.response?.data?.message || "Failed to send message");
    } finally {
      setChatSending(false);
    }
  };

  const formatTimeRemaining = (targetTimeMs) => {
    const diff = targetTimeMs - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const fetchSellerDisputes = async () => {
    try {
      const res = await sellerAPI.getDisputes();
      if (res.data?.success) {
        setSellerDisputes(res.data.data || []);
      }
    } catch (e) {
      console.error("Error fetching disputes:", e);
    }
  };

  const handleOpenDispute = (tx) => {
    setDisputeTx(tx);
    const amountVal = Math.abs(Number(tx.amount || 0));
    setDisputeCoins(String(amountVal || ''));
    const desc = tx.description || '';
    const match = desc.match(/XOXO:\s*([A-Za-z0-9_-]+)/i) || desc.match(/to\s+([^,(]+)/i);
    setDisputeUserId(match ? match[1].trim() : '');
    setDisputeReason('Payment screenshot was fake / edited');
    setDisputeCustomReason('');
    setDisputeProofUrl('');
    setProofFile(null);
    setProofPreview(null);
    setUploadingProof(false);
    setDisputeError('');
    setDisputeSuccess('');
  };

  const handleProofFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setDisputeError('Proof file size must be less than 10MB');
      return;
    }

    setProofFile(file);
    if (file.type.startsWith('image/')) {
      setProofPreview(URL.createObjectURL(file));
    } else {
      setProofPreview(null);
    }

    setUploadingProof(true);
    setDisputeError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await sellerAPI.uploadDisputeProof(formData);
      if (res.data?.success && res.data?.url) {
        setDisputeProofUrl(res.data.url);
      }
    } catch (err) {
      setDisputeError(err.response?.data?.message || 'Failed to upload proof file. You can still paste a link manually.');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleRemoveProofFile = () => {
    setProofFile(null);
    setProofPreview(null);
    setDisputeProofUrl('');
  };

  const handleSubmitDispute = async (e) => {
    e.preventDefault();
    if (!disputeUserId) {
      setDisputeError('Target user or XOXO ID is required');
      return;
    }
    const coins = parseInt(disputeCoins, 10);
    if (!coins || coins <= 0) {
      setDisputeError('Valid coin amount is required');
      return;
    }
    const finalReason = disputeReason === 'Other' ? disputeCustomReason : disputeReason;
    if (!finalReason) {
      setDisputeError('Please state the reason for payment dispute');
      return;
    }

    setDisputeLoading(true);
    setDisputeError('');
    try {
      const res = await sellerAPI.raiseDispute({
        user_id: disputeUserId,
        coins_amount: coins,
        reason: finalReason,
        proof_url: disputeProofUrl
      });
      if (res.data?.success) {
        setDisputeSuccess(res.data.message || 'Dispute ticket raised! User coins have been frozen.');
        fetchSellerDisputes();
        setTimeout(() => {
          setDisputeTx(null);
          setDisputeSuccess('');
        }, 2200);
      }
    } catch (err) {
      setDisputeError(err.response?.data?.message || err.message || 'Failed to raise dispute');
    } finally {
      setDisputeLoading(false);
    }
  };

  // Real-time debounce search recipient
  useEffect(() => {
    if (!recipientQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      searchTargetRecipient(recipientQuery.trim(), transferMode);
    }, 300);
    return () => clearTimeout(timer);
  }, [recipientQuery, transferMode]);

  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      const [statsRes, profileRes] = await Promise.all([
        sellerAPI.getDashboardStats(),
        sellerAPI.getProfile()
      ]);
      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
      if (profileRes.data?.success) {
        setSeller(profileRes.data.data);
      }
    } catch (err) {
      console.error("Error loading seller dashboard:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTx(true);
    try {
      const res = await sellerAPI.getTransactions({ search: searchTerm });
      if (res.data?.success) {
        setTransactions(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading transactions:", err);
    } finally {
      setLoadingTx(false);
    }
  };

  const searchTargetRecipient = async (query, mode) => {
    setSearchingRecipient(true);
    try {
      const res = await sellerAPI.searchRecipients(query, mode);
      if (res.data?.success) {
        setSearchResults(res.data.data || []);
      }
    } catch (err) {
      console.error("Search recipient error:", err);
    } finally {
      setSearchingRecipient(false);
    }
  };

  // 🔹 Comma Formatting Helpers
  const formatNumberWithCommas = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
    if (!numStr) return '';
    return Number(numStr).toLocaleString('en-IN');
  };

  const getRawNumber = (val) => {
    if (!val) return 0;
    const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
    return Number(numStr) || 0;
  };

  const formatAmountInWords = (num) => {
    const n = Number(num);
    if (!n || n <= 0) return '';
    if (n >= 10000000) {
      const cr = (n / 10000000).toFixed(2).replace(/\.00$/, '');
      return `${cr} Crore Coins (${n.toLocaleString('en-IN')})`;
    }
    if (n >= 100000) {
      const lk = (n / 100000).toFixed(2).replace(/\.00$/, '');
      return `${lk} Lakh Coins (${n.toLocaleString('en-IN')})`;
    }
    if (n >= 1000) {
      const th = (n / 1000).toFixed(2).replace(/\.00$/, '');
      return `${th} Thousand Coins (${n.toLocaleString('en-IN')})`;
    }
    return `${n.toLocaleString('en-IN')} Coins`;
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
    if (raw === '') {
      setTransferAmount('');
      return;
    }
    setTransferAmount(formatNumberWithCommas(raw));
  };

  const setQuickAmount = (val) => {
    setTransferAmount(formatNumberWithCommas(val));
  };

  const handleSelectRecipient = (recipient) => {
    setSelectedRecipient(recipient);
    setRecipientQuery(recipient.name);
    setSearchResults([]);
    setTransferErrorMsg('');
  };

  const handleModeChange = (mode) => {
    setTransferMode(mode);
    setSelectedRecipient(null);
    setRecipientQuery('');
    setSearchResults([]);
    setTransferErrorMsg('');
    setTransferSuccessMsg('');
  };

  const handleExecuteTransfer = async () => {
    const rawAmt = getRawNumber(transferAmount);
    if (!selectedRecipient || rawAmt <= 0) {
      setTransferErrorMsg(`Please select a valid ${transferMode === 'agency' ? 'Agency' : 'User / Host'} and enter a coin amount.`);
      return;
    }

    if (rawAmt > stats.current_balance) {
      setTransferErrorMsg(`Insufficient Balance! You have ${stats.current_balance.toLocaleString('en-IN')} Coins.`);
      return;
    }

    setTransferLoading(true);
    setTransferErrorMsg('');
    setTransferSuccessMsg('');

    try {
      const res = await sellerAPI.transferCoins({
        target_id: selectedRecipient.target_id || selectedRecipient.id,
        recipient_type: transferMode,
        amount: rawAmt,
        note: transferNote.trim()
      });

      if (res.data?.success) {
        setIsConfirmModalOpen(false);
        setTransferSuccessMsg(res.data.message || `Successfully deposited ${rawAmt.toLocaleString('en-IN')} coins to ${selectedRecipient.name}!`);
        setTransferAmount('');
        setTransferNote('');
        setSelectedRecipient(null);
        setRecipientQuery('');
        
        // Refresh balance & transactions
        fetchDashboardData();
        fetchTransactions();
      }
    } catch (err) {
      setTransferErrorMsg(err.response?.data?.message || err.message || "Failed to transfer coins.");
    } finally {
      setTransferLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (txFilter === 'AGENCY') return (t.description || '').toUpperCase().includes('AGENCY');
    if (txFilter === 'HOST') return (t.description || '').toUpperCase().includes('HOST') || (t.description || '').toUpperCase().includes('USER');
    return true;
  });

  return (
    <div className="space-y-8 font-['Outfit']">
      {/* Top Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-400/10 border border-amber-400/30 text-amber-400 tracking-wide">
              <Sparkles size={12} /> OFFICIAL COIN SELLER
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Coin Seller Portal
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Directly deposit coins into User Wallets, Host Wallets, and Agency Wallets with instant delivery.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={openRechargeModal}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-950 font-black text-sm shadow-xl shadow-amber-500/25 active:scale-95 transition cursor-pointer"
          >
            <Zap size={16} className="fill-slate-950" />
            <span>Recharge Coins (Buy Slabs)</span>
          </button>
          <button
            onClick={() => { fetchDashboardData(); fetchTransactions(); }}
            disabled={refreshing}
            className="self-start md:self-auto flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm transition cursor-pointer"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin text-amber-400' : ''} />
            <span>Refresh Balance</span>
          </button>
        </div>
      </div>

      {/* Hero Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* 1. Live Balance Card (Hero) */}
        <div className="md:col-span-2 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-amber-950/40 border-2 border-amber-400/30 rounded-[30px] p-6 md:p-8 relative overflow-hidden shadow-2xl shadow-amber-500/10">
          <div className="absolute top-0 right-0 w-48 h-48 bg-amber-400/10 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-2xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center">
                <Coins size={22} className="text-amber-400" />
              </div>
              <span className="text-xs font-black tracking-widest text-amber-300 uppercase">Available Seller Coins</span>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-black bg-amber-400 text-slate-950">
              LIVE
            </span>
          </div>

          <div className="flex items-baseline gap-2">
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              {Number(stats.current_balance).toLocaleString('en-IN')}
            </h2>
            <span className="text-lg font-bold text-amber-400">Coins</span>
          </div>

          <p className="text-xs text-amber-200/70 font-medium mt-2">
            {formatAmountInWords(stats.current_balance)}
          </p>

          <div className="mt-6 pt-5 border-t border-amber-400/20 flex flex-col sm:flex-row sm:items-center justify-end gap-3 text-xs text-slate-300">
            {/* Seller Tier & Commission - Commented out as requested */}
            {/*
            <div className="flex items-center gap-4">
              <span>Seller Tier: <strong className="text-amber-400 font-extrabold">{stats.seller_level}</strong></span>
              <span>Commission: <strong className="text-white font-extrabold">{stats.commission_percentage}%</strong></span>
            </div>
            */}
            <button
              onClick={openRechargeModal}
              className="w-full sm:w-auto px-4 py-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-md shadow-amber-500/20 active:scale-95 transition cursor-pointer"
            >
              <PlusCircle size={14} />
              <span>Recharge My Wallet</span>
            </button>
          </div>
        </div>

        {/* 2. Today Sales */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-400 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider">Today's Transfers</span>
              <div className="w-8 h-8 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400">
                <TrendingUp size={16} />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white">
              {Number(stats.today_sold).toLocaleString('en-IN')}
            </h3>
            <p className="text-xs text-green-400 font-bold mt-1">
              +{stats.today_transfers} Transfers Today
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Reset every midnight IST
          </p>
        </div>

        {/* 3. Total Sales (All Time) */}
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[30px] p-6 relative overflow-hidden flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-slate-400 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider">Total Lifetime Coins</span>
              <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                <Award size={16} />
              </div>
            </div>
            <h3 className="text-2xl font-black text-white">
              {Number(stats.total_sold).toLocaleString('en-IN')}
            </h3>
            <p className="text-xs text-purple-400 font-bold mt-1">
              {stats.total_transfers} Total Deposits
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-4">
            Lifetime business volume
          </p>
        </div>
      </div>

      {/* Navigation Tab Bar */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-900/60 p-2 rounded-2xl border border-white/10 backdrop-blur-xl">
        <button
          onClick={() => setActiveSection('withdrawals')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-xs md:text-sm transition cursor-pointer ${
            activeSection === 'withdrawals'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-lg shadow-emerald-500/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <ArrowDownCircle size={17} />
          <span>User Withdrawal Requests</span>
          {withdrawals.filter(w => w.status === 'pending_seller' || w.status === 'pending' || w.status === 'accepted_by_seller').length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-black bg-slate-950 text-emerald-400">
              {withdrawals.filter(w => w.status === 'pending_seller' || w.status === 'pending' || w.status === 'accepted_by_seller').length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveSection('deposit')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-xs md:text-sm transition cursor-pointer ${
            activeSection === 'deposit'
              ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 shadow-lg shadow-amber-500/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <Send size={16} />
          <span>Direct Coin Deposit</span>
        </button>

        <button
          onClick={() => setActiveSection('rates')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-xs md:text-sm transition cursor-pointer ${
            activeSection === 'rates'
              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <Settings size={16} />
          <span>My Selling Price Rate</span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-black bg-white/20 text-white">
            🪙 {rateData.my_coins_per_rupee}/₹1
          </span>
        </button>

        <button
          onClick={() => setActiveSection('ledger')}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-xs md:text-sm transition cursor-pointer ${
            activeSection === 'ledger'
              ? 'bg-white/15 text-white border border-white/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <History size={16} />
          <span>Deposit Ledger</span>
        </button>
      </div>

      {/* SECTION 1: User Withdrawal Payout Requests */}
      {activeSection === 'withdrawals' && (
        <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/10">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 tracking-wider">
                  Live Orders
                </span>
              </div>
              <h2 className="text-2xl font-black text-white flex items-center gap-2">
                <ArrowDownCircle className="text-emerald-400" size={24} />
                User Withdrawal Payout Requests
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Users selected you from the mobile app Top Sellers list to withdraw cash. Pay the user via UPI/Bank and receive their coins into your wallet.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex bg-slate-950/60 p-1 rounded-xl border border-white/10 flex-wrap">
                {[
                  { id: 'pending', label: 'Action Required' },
                  { id: 'accepted_by_seller', label: 'In Progress' },
                  { id: 'seller_paid_pending_confirmation', label: 'Awaiting User' },
                  { id: 'disputed', label: 'Disputed ⚠️' },
                  { id: 'paid', label: 'Completed' },
                  { id: 'all', label: 'All Requests' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setWithdrawalFilter(tab.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      withdrawalFilter === tab.id ? 'bg-emerald-500 text-slate-950 shadow-sm' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                onClick={fetchWithdrawalRequests}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-slate-300 transition cursor-pointer"
                title="Refresh"
              >
                <RefreshCw size={16} className={loadingWithdrawals ? 'animate-spin text-emerald-400' : ''} />
              </button>
            </div>
          </div>

          {loadingWithdrawals ? (
            <div className="p-16 text-center">
              <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-slate-400 text-sm">Loading withdrawal requests...</p>
            </div>
          ) : withdrawals.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <ArrowDownCircle size={48} className="mx-auto mb-3 opacity-30" />
              <h3 className="text-white font-bold text-base mb-1">No Withdrawal Requests Found</h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                {withdrawalFilter === 'pending'
                  ? 'No pending withdrawal requests at the moment. When users choose you in the mobile app, their payout orders will appear here.'
                  : 'No orders found matching this filter.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {withdrawals.map((req) => {
                const isPending = req.status === 'pending_seller' || req.status === 'pending';
                const isAccepted = req.status === 'accepted_by_seller';
                const isPaidPending = req.status === 'seller_paid_pending_confirmation';
                const isDisputed = req.status === 'disputed';
                const isPaid = req.status === 'paid';
                const isRejected = req.status === 'rejected';

                const acceptTimeLeft = req.created_at ? formatTimeRemaining(new Date(req.created_at).getTime() + 60 * 60 * 1000) : null;
                const payTimeLeft = (req.accepted_at || req.created_at) ? formatTimeRemaining(new Date(req.accepted_at || req.created_at).getTime() + 24 * 60 * 60 * 1000) : null;
                const confirmTimeLeft = req.seller_paid_at ? formatTimeRemaining(new Date(req.seller_paid_at).getTime() + 24 * 60 * 60 * 1000) : null;

                return (
                  <div
                    key={req.id}
                    className={`rounded-2xl border p-5 md:p-6 transition-all ${
                      isPending
                        ? 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50'
                        : isAccepted
                        ? 'bg-blue-500/5 border-blue-500/30 hover:border-blue-500/50'
                        : isPaidPending
                        ? 'bg-purple-500/5 border-purple-500/30 hover:border-purple-500/50'
                        : isDisputed
                        ? 'bg-rose-500/10 border-rose-500/40 hover:border-rose-500/60'
                        : isPaid
                        ? 'bg-emerald-500/5 border-emerald-500/20'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                      {/* Left: User details */}
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center font-black text-white text-lg">
                          {req.avatar_url ? (
                            <img src={req.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            req.first_name?.[0] || 'U'
                          )}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="text-white font-black text-base">{req.user_name}</h4>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-white/10 text-slate-300">
                              XOXO: {req.xoxo_id || 'N/A'}
                            </span>
                            {req.country && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/10 text-slate-300">
                                🌍 {req.country}
                              </span>
                            )}
                            {isPending && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-amber-500 text-slate-950 animate-pulse">
                                Action Needed • ⏳ Accept in {acceptTimeLeft}
                              </span>
                            )}
                            {isAccepted && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-blue-500 text-white">
                                In Progress • ⏳ Pay in {payTimeLeft}
                              </span>
                            )}
                            {isPaidPending && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-purple-500/20 text-purple-300 border border-purple-500/40">
                                🪙 Hold: {Number(req.seller_hold_coins || req.coins || 0).toLocaleString()} Coins • Auto-confirms in {confirmTimeLeft}
                              </span>
                            )}
                            {isDisputed && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-rose-500 text-white animate-pulse">
                                Disputed ⚠️ Support Ticket #{req.ticket_id ? String(req.ticket_id).substring(0, 8) : 'Pending'}
                              </span>
                            )}
                            {isPaid && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-500 text-slate-950">
                                Completed & Paid
                              </span>
                            )}
                            {isRejected && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-rose-500 text-white">
                                Declined
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Requested: {req.created_at ? new Date(req.created_at).toLocaleString('en-IN') : '—'}
                          </p>

                          {/* Payout Details (UPI or Bank) */}
                          <div className="mt-3 p-3 bg-slate-950/60 rounded-xl border border-white/10 text-xs text-slate-300 space-y-1">
                            <div className="flex items-center justify-between text-[11px] text-slate-400 font-bold uppercase tracking-wider mb-1">
                              <span>Payout Destination ({req.payout_type?.toUpperCase()})</span>
                            </div>

                            {req.payout_type === 'upi' ? (
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-white font-mono font-bold text-sm">{req.upi_id}</span>
                                <button
                                  onClick={() => copyToClipboard(req.upi_id, `upi_${req.id}`)}
                                  className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white transition flex items-center gap-1 font-bold text-[11px] cursor-pointer"
                                >
                                  {copiedKey === `upi_${req.id}` ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                                  <span>{copiedKey === `upi_${req.id}` ? 'Copied' : 'Copy UPI'}</span>
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span>A/C Holder: <strong className="text-white">{req.account_holder_name}</strong></span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <span>A/C No: <strong className="text-white font-mono">{req.account_number}</strong></span>
                                  <button
                                    onClick={() => copyToClipboard(req.account_number, `acc_${req.id}`)}
                                    className="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 text-slate-300 transition text-[10px] font-bold cursor-pointer"
                                  >
                                    {copiedKey === `acc_${req.id}` ? 'Copied' : 'Copy'}
                                  </button>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <span>IFSC: <strong className="text-white font-mono">{req.ifsc_code}</strong></span>
                                  <button
                                    onClick={() => copyToClipboard(req.ifsc_code, `ifsc_${req.id}`)}
                                    className="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 text-slate-300 transition text-[10px] font-bold cursor-pointer"
                                  >
                                    {copiedKey === `ifsc_${req.id}` ? 'Copied' : 'Copy'}
                                  </button>
                                </div>
                              </div>
                            )}

                            {(req.transaction_ref || isPaidPending || isDisputed) && (
                              <div className="pt-2 mt-2 border-t border-white/10 text-xs space-y-1">
                                {req.transaction_ref && (
                                  <div className="text-emerald-400">
                                    <strong>UTR / Txn Ref:</strong> {req.transaction_ref} {req.seller_paid_at && `• ${new Date(req.seller_paid_at).toLocaleTimeString()}`}
                                  </div>
                                )}
                                {isPaidPending && (
                                  <div className="text-purple-300 text-[11px]">
                                    ℹ️ Payment marked sent. Awaiting user verification (YES/NO). Hold coins will be released upon confirmation or after 24 hrs.
                                  </div>
                                )}
                                {isDisputed && req.dispute_reason && (
                                  <div className="text-rose-400 text-[11px] font-semibold">
                                    ⚠️ Dispute Note: {req.dispute_reason}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Amounts & Actions */}
                      <div className="flex flex-col sm:flex-row lg:flex-col items-start lg:items-end justify-between gap-3 shrink-0">
                        <div className="text-left lg:text-right">
                          <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Amount to Pay User</span>
                          <p className="text-2xl font-black text-emerald-400">
                            ₹{Number(req.amount_inr || 0).toLocaleString('en-IN')}
                          </p>
                          <span className="text-xs text-amber-300 font-bold block mt-0.5">
                            You Receive: 🪙 {Number(req.coins || 0).toLocaleString('en-IN')} Coins
                          </span>
                        </div>

                        {/* Action Buttons & Direct Chat */}
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                          {/* Direct Chat with User button */}
                          <button
                            type="button"
                            onClick={() => openChatWithUser({
                              user_id: req.user_id,
                              user_name: req.user_name || 'User',
                              avatar_url: req.avatar_url,
                              xoxo_id: req.xoxo_id,
                              is_disputed: isDisputed,
                              dispute_reason: req.dispute_reason,
                              ticket_id: req.ticket_id,
                              amount_inr: req.amount_inr,
                              withdrawal_id: req.id
                            })}
                            className={`px-3 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                              isDisputed
                                ? 'bg-rose-500/25 hover:bg-rose-500/35 text-rose-300 border border-rose-500/50 shadow-sm animate-pulse'
                                : 'bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30'
                            }`}
                            title={isDisputed ? "Payment Disputed: Chat directly with User" : "Direct Chat with User"}
                          >
                            <MessageSquare size={13} />
                            <span>{isDisputed ? '⚠️ Chat with User (Disputed)' : 'Chat with User'}</span>
                          </button>

                          {isPending && (
                            <>
                              <button
                                onClick={() => handleAcceptWithdrawal(req.id)}
                                className="flex-1 sm:flex-initial px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs rounded-xl shadow-lg shadow-emerald-500/20 transition flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <Check size={14} />
                                <span>Accept (1h)</span>
                              </button>
                              <button
                                onClick={() => setDeclinePromptId(req.id)}
                                className="px-3 py-2 bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-white/10 rounded-xl text-xs font-bold transition cursor-pointer"
                              >
                                Decline
                              </button>
                            </>
                          )}

                          {isAccepted && (
                            <>
                              <button
                                onClick={() => {
                                  setPayingWithdrawal(req);
                                  setPaymentRef('');
                                  setPaymentNotes('');
                                  setPaymentError('');
                                  setPaymentSuccess('');
                                }}
                                className="flex-1 sm:flex-initial px-4 py-2 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-slate-950 font-black text-xs rounded-xl shadow-lg shadow-amber-500/20 transition flex items-center justify-center gap-1.5 cursor-pointer animate-pulse"
                              >
                                <DollarSign size={14} />
                                <span>Pay User (24h)</span>
                              </button>
                              <button
                                onClick={() => setDeclinePromptId(req.id)}
                                className="px-3 py-2 bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-white/10 rounded-xl text-xs font-bold transition cursor-pointer"
                              >
                                Decline
                              </button>
                            </>
                          )}

                          {isPaidPending && (
                            <span className="px-3 py-1.5 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-300 font-bold text-xs flex items-center gap-1.5">
                              <Clock size={14} /> Coins on Hold (Awaiting User)
                            </span>
                          )}

                          {isDisputed && (
                            <span className="px-3 py-1.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400 font-bold text-xs flex items-center gap-1.5">
                              <AlertCircle size={14} /> Ticket Under Review
                            </span>
                          )}

                          {isPaid && (
                            <span className="px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold text-xs flex items-center gap-1.5">
                              <CheckCircle size={14} /> Coins Added to Wallet
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SECTION 2: My Selling Price Rate Settings */}
      {activeSection === 'rates' && (
        <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl space-y-6">
          <div className="pb-6 border-b border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 tracking-wider">
                Seller Competitive Pricing
              </span>
            </div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2">
              <Settings className="text-indigo-400" size={24} />
              My Coin Selling Price Rate
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Set how many coins you will offer per 1 INR (₹1). Users will see your rate in the mobile app Top Sellers list.
            </p>
          </div>

          {/* Admin Policy Ceiling Alert */}
          <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border border-amber-400/30 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center text-amber-400 shrink-0">
              <ShieldCheck size={20} />
            </div>
            <div className="space-y-1">
              <h4 className="text-sm font-black text-white flex items-center gap-2">
                <span>Admin Maximum Ceiling:</span>
                <span className="text-amber-400 font-mono text-base">Max {rateData.max_coins_per_rupee} Coins / ₹1</span>
              </h4>
              <p className="text-xs text-slate-300">
                You cannot offer more than <strong>{rateData.max_coins_per_rupee} coins per ₹1</strong>. However, you are free to set your own price (e.g. 15 or 18 coins/₹1) to offer a competitive deal and win more buyers!
              </p>
            </div>
          </div>

          {rateFeedback.message && (
            <div className={`p-4 rounded-xl border flex items-center gap-2 text-sm font-bold animate-in fade-in ${
              rateFeedback.type === 'success'
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
            }`}>
              {rateFeedback.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span>{rateFeedback.message}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-2">
            {/* Rate Input Box */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-300 mb-2">
                  Enter Your Selling Rate (Coins per ₹1)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-amber-400 font-bold">
                    🪙
                  </div>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    max={rateData.max_coins_per_rupee}
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    className="w-full pl-11 pr-24 py-4 bg-white/5 border border-white/10 rounded-2xl text-white font-black text-2xl focus:outline-none focus:border-indigo-400"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400 text-xs font-bold">
                    Coins / ₹1
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  Mobile app users see this as: <strong className="text-white">"{rateInput || 20} Coins / ₹1"</strong>
                </p>
              </div>

              {/* Quick Preset Buttons */}
              <div className="space-y-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Quick Presets:</span>
                <div className="flex flex-wrap gap-2">
                  {[15, 16, 17, 18, 19, rateData.max_coins_per_rupee].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setRateInput(String(preset))}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition cursor-pointer ${
                        parseFloat(rateInput) === preset
                          ? 'bg-indigo-500 text-white border-indigo-400'
                          : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {preset} Coins / ₹1 {preset === rateData.max_coins_per_rupee && '(Max)'}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSaveRate}
                disabled={savingRate}
                className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 transition flex items-center justify-center gap-2 cursor-pointer mt-4"
              >
                {savingRate ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Check size={18} />
                    <span>Save My Selling Rate</span>
                  </>
                )}
              </button>
            </div>

            {/* Live Calculation Preview */}
            <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-6 space-y-4 flex flex-col justify-between">
              <div>
                <h4 className="text-sm font-black text-white uppercase tracking-wider mb-1 flex items-center gap-2">
                  <Coins size={16} className="text-amber-400" />
                  Live Customer Pricing Preview
                </h4>
                <p className="text-xs text-slate-400 mb-4">
                  Based on your rate of <strong className="text-indigo-400">{rateInput || 20} Coins per ₹1</strong>:
                </p>

                <div className="space-y-2.5 text-xs">
                  {[
                    { inr: 20 },
                    { inr: 50 },
                    { inr: 100 },
                    { inr: 500 },
                    { inr: 1000 }
                  ].map(row => {
                    const r = parseFloat(rateInput) || 20;
                    const coins = Math.round(row.inr * r);
                    return (
                      <div key={row.inr} className="flex justify-between items-center p-2.5 rounded-xl bg-white/5 border border-white/5">
                        <span className="font-bold text-slate-300">User pays ₹{row.inr}:</span>
                        <span className="font-black text-amber-400 text-sm">
                          🪙 {coins.toLocaleString('en-IN')} Coins
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="text-[11px] text-slate-500 italic mt-3">
                * When users withdraw or purchase coins, calculations will automatically use your active rate.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 3: Direct Coin Deposit */}
      {activeSection === 'deposit' && (
      <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-slate-950 font-black">
              <Send size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white">Direct Coin Deposit</h2>
              <p className="text-sm text-slate-400">Select whether you want to deposit coins into an Agency or a User / Host.</p>
            </div>
          </div>

          {/* Mode Switcher Tabs */}
          <div className="flex bg-slate-950/60 p-1.5 rounded-2xl border border-white/10 self-start">
            <button
              type="button"
              onClick={() => handleModeChange('agency')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition ${
                transferMode === 'agency'
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 shadow-lg shadow-amber-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Building2 size={16} />
              <span>Deposit to Agency</span>
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('host')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition ${
                transferMode === 'host'
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/20'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Radio size={16} />
              <span>Deposit to User / Host</span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {transferSuccessMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-green-500/10 border border-green-500/25 text-green-400 font-bold flex items-center gap-3 animate-in fade-in">
            <CheckCircle size={22} className="shrink-0" />
            <span>{transferSuccessMsg}</span>
          </div>
        )}
        {transferErrorMsg && (
          <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/25 text-red-400 font-bold flex items-center gap-3 animate-in fade-in">
            <AlertCircle size={22} className="shrink-0" />
            <span>{transferErrorMsg}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Search & Recipient Selection */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                1. Search {transferMode === 'agency' ? 'Agency (Name / Code / ID)' : 'User / Host (XOXO ID / Name / Phone)'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Search size={18} />
                </div>
                <input
                  type="text"
                  value={recipientQuery}
                  onChange={(e) => {
                    setRecipientQuery(e.target.value);
                    if (selectedRecipient) setSelectedRecipient(null);
                  }}
                  placeholder={
                    transferMode === 'agency' 
                      ? "Search Agency Name, Agency Code or ID..." 
                      : "Search User / Host by XOXO ID, Name, Phone..."
                  }
                  className="w-full pl-11 pr-10 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium text-sm"
                />
                {searchingRecipient && (
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-[#0c1227] border border-white/10 rounded-2xl p-2 shadow-2xl space-y-1 max-h-64 overflow-y-auto">
                  {searchResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleSelectRecipient(item)}
                      className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition text-left group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white overflow-hidden ${
                          item.recipient_type === 'agency' 
                            ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400' 
                            : 'bg-pink-500/20 border border-pink-500/30 text-pink-400'
                        }`}>
                          {item.avatar_url ? (
                            <img src={item.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            item.recipient_type === 'agency' ? <Building2 size={18} /> : <Radio size={18} />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-white text-sm group-hover:text-amber-400 transition">{item.name}</p>
                          <p className="text-xs text-slate-400">{item.sub_title}</p>
                        </div>
                      </div>
                      {transferMode === 'agency' && item.balance !== undefined ? (
                        <div className="text-right">
                          <span className="text-[10px] uppercase font-bold text-slate-400">Current Balance:</span>
                          <p className="font-bold text-sm text-white">🪙 {Number(item.balance).toLocaleString('en-IN')}</p>
                        </div>
                      ) : (
                        <div className="text-right">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-pink-500/20 text-pink-400 border border-pink-500/30">
                            Verified User
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Recipient Preview Card */}
            {selectedRecipient ? (
              <div className={`border rounded-2xl p-5 animate-in fade-in ${
                transferMode === 'agency' 
                  ? 'bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30'
                  : 'bg-gradient-to-br from-pink-500/10 to-rose-500/10 border-pink-500/30'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-black uppercase tracking-wider flex items-center gap-1.5 ${
                    transferMode === 'agency' ? 'text-amber-300' : 'text-pink-300'
                  }`}>
                    <CheckCircle size={14} /> Confirmed {transferMode === 'agency' ? 'Agency' : 'Host'} Recipient
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedRecipient(null); setRecipientQuery(''); }}
                    className="text-xs text-slate-400 hover:text-white underline font-bold"
                  >
                    Change
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl overflow-hidden border ${
                    transferMode === 'agency' 
                      ? 'bg-amber-400/20 border-amber-400/40 text-amber-300'
                      : 'bg-pink-400/20 border-pink-400/40 text-pink-300'
                  }`}>
                    {selectedRecipient.avatar_url ? (
                      <img src={selectedRecipient.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      transferMode === 'agency' ? <Building2 size={24} /> : <Radio size={24} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-black text-white">{selectedRecipient.name}</h4>
                    <p className="text-xs text-slate-300">{selectedRecipient.sub_title}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                      {transferMode === 'agency' && selectedRecipient.balance !== undefined ? (
                        <span>Agency Live Wallet: <strong className="text-amber-400 font-bold">🪙 {Number(selectedRecipient.balance).toLocaleString('en-IN')}</strong></span>
                      ) : (
                        <span className="text-emerald-400 font-bold flex items-center gap-1">
                          <CheckCircle size={13} /> Account Verified & Ready for Recharge
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-white/10 rounded-2xl p-6 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                {transferMode === 'agency' ? <Building2 size={28} className="text-slate-600" /> : <Radio size={28} className="text-slate-600" />}
                <p>Search and select an {transferMode === 'agency' ? 'Agency' : 'User / Host'} from above to begin deposit.</p>
              </div>
            )}
          </div>

          {/* Right Column: Amount & Action */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                2. Enter Coin Deposit Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-amber-400 font-bold">
                  🪙
                </div>
                <input
                  type="text"
                  value={transferAmount}
                  onChange={handleAmountChange}
                  placeholder="e.g. 500,000"
                  className="w-full pl-11 pr-24 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-black text-xl tracking-wide"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400 text-xs font-bold">
                  COINS
                </div>
              </div>

              {/* Amount in words helper */}
              {transferAmount && (
                <p className="text-xs text-amber-400 font-bold mt-2 pl-1">
                  ⚡ {formatAmountInWords(getRawNumber(transferAmount))}
                </p>
              )}

              {/* Quick Amount Chips */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                {[50000, 100000, 500000, 1000000, 5000000, 10000000].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setQuickAmount(amt)}
                    className="px-2 py-2 rounded-xl bg-white/5 hover:bg-amber-400/20 border border-white/10 hover:border-amber-400/30 text-white hover:text-amber-300 font-bold text-xs transition text-center"
                  >
                    +{amt >= 1000000 ? `${amt / 1000000}M` : `${amt / 1000}K`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                3. Deposit Note / Reference (Optional)
              </label>
              <input
                type="text"
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder={`e.g. Monthly ${transferMode === 'agency' ? 'Agency' : 'Host'} Coin Allocation`}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition font-medium text-sm"
              />
            </div>

            <button
              type="button"
              disabled={!selectedRecipient || !transferAmount || transferLoading}
              onClick={() => setIsConfirmModalOpen(true)}
              className={`w-full py-4 rounded-2xl font-black text-base transition flex items-center justify-center gap-2 shadow-xl ${
                !selectedRecipient || !transferAmount || transferLoading
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : transferMode === 'agency'
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 shadow-amber-500/20'
                  : 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white shadow-pink-500/20'
              }`}
            >
              <Send size={18} />
              <span>Deposit to {transferMode === 'agency' ? 'Agency' : 'User / Host'} Now</span>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-white/20 rounded-[32px] p-6 md:p-8 max-w-md w-full shadow-2xl relative animate-in zoom-in-95">
            <div className="w-14 h-14 rounded-2xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 mx-auto mb-4">
              <Coins size={28} />
            </div>

            <h3 className="text-2xl font-black text-white text-center">
              Confirm Coin Deposit
            </h3>
            <p className="text-xs text-slate-400 text-center mt-1 mb-6">
              Please double check the recipient and amount before finalizing.
            </p>

            <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 space-y-3 mb-6">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Recipient Type:</span>
                <span className="font-extrabold uppercase text-amber-400">{transferMode}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Target Name:</span>
                <span className="font-bold text-white">{selectedRecipient?.name}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Deposit Amount:</span>
                <span className="font-black text-amber-400 text-sm">
                  🪙 {formatNumberWithCommas(transferAmount)} Coins
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Amount In Words:</span>
                <span className="font-bold text-slate-300 text-[11px] text-right">
                  {formatAmountInWords(getRawNumber(transferAmount))}
                </span>
              </div>
              <div className="pt-2 border-t border-white/10 flex justify-between text-xs">
                <span className="text-slate-400">Balance After Deposit:</span>
                <span className="font-extrabold text-white">
                  🪙 {(stats.current_balance - getRawNumber(transferAmount)).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsConfirmModalOpen(false)}
                disabled={transferLoading}
                className="flex-1 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteTransfer}
                disabled={transferLoading}
                className={`flex-1 py-3.5 rounded-xl font-black text-sm transition flex items-center justify-center gap-2 ${
                  transferMode === 'agency'
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950'
                    : 'bg-gradient-to-r from-pink-500 to-rose-500 text-white'
                }`}
              >
                {transferLoading ? (
                  <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Check size={18} />
                    <span>Confirm & Deposit</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4: Transaction History Section */}
      {activeSection === 'ledger' && (
      <div className="bg-slate-900/70 backdrop-blur-2xl border border-white/10 rounded-[32px] p-6 md:p-10 shadow-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-2">
              <History size={22} className="text-amber-400" />
              Deposit History & Activity
            </h2>
            <p className="text-xs text-slate-400 mt-1">Audit log of all coins deposited into agencies and hosts.</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowDisputesList(true)}
              className="px-3 py-1.5 bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm"
              title="View raised fake payment dispute tickets"
            >
              <ShieldAlert size={14} className="text-rose-400" />
              <span>Raised Disputes</span>
              {sellerDisputes.length > 0 && (
                <span className="px-1.5 py-0.2 text-[10px] bg-rose-500 text-white rounded-full font-black">
                  {sellerDisputes.length}
                </span>
              )}
            </button>

            <div className="flex bg-slate-950/60 p-1 rounded-xl border border-white/10">
              {['ALL', 'AGENCY', 'HOST'].map((f) => (
                <button
                  key={f}
                  onClick={() => setTxFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    txFilter === f ? 'bg-amber-400 text-slate-950' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <button
              onClick={fetchTransactions}
              className="p-2 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-slate-300 transition"
              title="Refresh ledger"
            >
              <RefreshCw size={16} className={loadingTx ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {loadingTx ? (
          <div className="p-16 text-center">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-slate-400 text-sm">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-16 text-center text-slate-500 text-sm">
            <Coins className="mx-auto mb-3 opacity-30" size={40} />
            <p>No deposit transactions found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-slate-400 text-xs font-black uppercase tracking-wider">
                  <th className="py-4 px-4">Date & Time</th>
                  <th className="py-4 px-4">Type</th>
                  <th className="py-4 px-4">Description</th>
                  <th className="py-4 px-4 text-right">Amount (Coins)</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4 text-center">Dispute Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-sm">
                {filteredTransactions.map((tx) => {
                  const isDeposit = tx.transaction_type === 'SELLER_DEPOSIT' || tx.transaction_type === 'COIN_DEDUCT';
                  const isAgency = (tx.description || '').toUpperCase().includes('AGENCY');

                  return (
                    <tr key={tx.id} className="hover:bg-white/5 transition">
                      <td className="py-4 px-4 text-slate-400 text-xs font-medium">
                        {tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                          isAgency
                            ? 'bg-amber-400/10 text-amber-300 border border-amber-400/20'
                            : 'bg-pink-400/10 text-pink-300 border border-pink-400/20'
                        }`}>
                          {isAgency ? <Building2 size={12} /> : <Radio size={12} />}
                          {isAgency ? 'Agency Deposit' : 'Host Deposit'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-300 font-medium">
                        {tx.description || 'Coin transfer'}
                      </td>
                      <td className="py-4 px-4 text-right font-black text-amber-400">
                        -🪙 {Number(tx.amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-green-500/10 text-green-400 border border-green-500/20">
                          {tx.status || 'COMPLETED'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleOpenDispute(tx)}
                          className="px-2.5 py-1 bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 border border-rose-500/30 rounded-lg text-xs font-bold transition flex items-center gap-1 mx-auto"
                          title="Report fake/missing payment to freeze recipient coins"
                        >
                          <ShieldAlert size={12} />
                          <span>Report Fake</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Payout & Coin Collection Modal */}
      {payingWithdrawal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-emerald-500/30 rounded-[32px] p-6 md:p-8 max-w-lg w-full shadow-2xl relative animate-in zoom-in-95">
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mx-auto mb-4">
              <DollarSign size={28} />
            </div>

            <h3 className="text-2xl font-black text-white text-center">
              Fulfill Payout & Collect Coins
            </h3>
            <p className="text-xs text-slate-400 text-center mt-1 mb-5">
              Confirm that you have transferred ₹{payingWithdrawal.amount_inr} to the user. On submit, <strong className="text-amber-400">🪙 {Number(payingWithdrawal.coins).toLocaleString('en-IN')} Coins</strong> will be credited directly into your seller wallet!
            </p>

            {paymentError && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/25 text-red-400 font-bold text-xs flex items-center gap-2">
                <AlertCircle size={16} />
                <span>{paymentError}</span>
              </div>
            )}

            {paymentSuccess && (
              <div className="mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/25 text-green-400 font-bold text-xs flex items-center gap-2">
                <CheckCircle size={16} />
                <span>{paymentSuccess}</span>
              </div>
            )}

            {/* Recipient Details Box */}
            <div className="bg-slate-950/70 border border-white/10 rounded-2xl p-4 space-y-2.5 mb-5 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">User:</span>
                <span className="font-bold text-white">{payingWithdrawal.user_name} (XOXO: {payingWithdrawal.xoxo_id})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Payout Method:</span>
                <span className="font-bold uppercase text-emerald-400">{payingWithdrawal.payout_type}</span>
              </div>
              {payingWithdrawal.payout_type === 'upi' ? (
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">UPI ID:</span>
                  <span className="font-bold text-white font-mono">{payingWithdrawal.upi_id}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Bank Account:</span>
                    <span className="font-bold text-white font-mono">{payingWithdrawal.account_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">IFSC Code:</span>
                    <span className="font-bold text-white font-mono">{payingWithdrawal.ifsc_code}</span>
                  </div>
                </>
              )}
              <div className="pt-2 border-t border-white/10 flex justify-between">
                <span className="text-slate-400">You Must Pay User:</span>
                <span className="font-black text-emerald-400 text-sm">₹{payingWithdrawal.amount_inr}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Coins Credited To You:</span>
                <span className="font-black text-amber-400 text-sm">🪙 {Number(payingWithdrawal.coins).toLocaleString('en-IN')} Coins</span>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-300 mb-1.5">
                  Payment Reference / UTR Number <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  placeholder="e.g. UPI Ref / UTR 324567891234"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400 font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-300 mb-1.5">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g. Paid via Google Pay / PhonePe"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400 text-xs"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setPayingWithdrawal(null)}
                  disabled={paymentSubmitting}
                  className="flex-1 py-3.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  disabled={paymentSubmitting || !paymentRef.trim()}
                  className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs transition shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {paymentSubmitting ? (
                    <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Check size={16} />
                      <span>Confirm & Collect Coins</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Prompt Modal */}
      {declinePromptId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-white/20 rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95">
            <h4 className="text-lg font-black text-white mb-2">Decline Withdrawal Request?</h4>
            <p className="text-xs text-slate-400 mb-4">
              Declining will refund the coins back to the user's wallet. Please specify a reason:
            </p>
            <input
              type="text"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. Bank servers down, invalid UPI ID"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-xs mb-4 focus:outline-none focus:border-red-400"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeclinePromptId(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeclineWithdrawal(declinePromptId)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-500 text-white shadow-sm cursor-pointer"
              >
                Decline & Refund User
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Direct User Chat Modal */}
      {chatUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col h-[520px] animate-in zoom-in-95">
            {/* Header */}
            <div className="p-4 bg-slate-950/80 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-pink-500 to-purple-500 text-white flex items-center justify-center font-bold overflow-hidden shadow-inner">
                  {chatUser.avatar_url ? (
                    <img src={chatUser.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (chatUser.user_name || 'U').charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm flex items-center gap-2">
                    {chatUser.user_name}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      chatUser.is_disputed 
                        ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40' 
                        : 'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {chatUser.is_disputed ? '⚠️ Payment Disputed' : 'Direct Chat'}
                    </span>
                  </h4>
                  <p className="text-[11px] text-slate-400">XOXO ID: {chatUser.xoxo_id || chatUser.user_id?.substring(0, 8)}</p>
                </div>
              </div>
              <button
                onClick={() => setChatUser(null)}
                className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Dispute Warning Notice Banner */}
            {chatUser.is_disputed && (
              <div className="p-2.5 px-4 bg-rose-500/20 border-b border-rose-500/30 text-[11px] text-rose-200 flex items-center gap-2">
                <AlertCircle size={14} className="text-rose-400 shrink-0" />
                <span>
                  User reported payment not received (Ticket #{chatUser.ticket_id ? String(chatUser.ticket_id).substring(0, 8) : 'Disputed'}). Share UTR or proof to resolve.
                </span>
              </div>
            )}

            {/* Messages body */}
            <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-slate-900/50">
              {chatLoading ? (
                <div className="flex items-center justify-center h-full text-slate-400 text-xs gap-2">
                  <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading messages...</span>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center p-6">
                  <MessageSquare size={36} className="opacity-30 mb-2" />
                  <p className="text-xs font-semibold text-slate-300">No conversation yet</p>
                  <p className="text-[11px] text-slate-500 mt-1">Send a message to clarify payment details, UTR numbers, or account info directly.</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => {
                  const isSeller = String(msg.sender_id) === String(seller?.user_id) || String(msg.sender_id) === String(seller?.id);
                  return (
                    <div key={msg.id || idx} className={`flex ${isSeller ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-xs ${
                        isSeller 
                          ? 'bg-indigo-600 text-white rounded-br-none shadow-md shadow-indigo-600/20' 
                          : 'bg-white/10 text-slate-100 rounded-bl-none border border-white/10'
                      }`}>
                        <p className="leading-relaxed whitespace-pre-wrap">{msg.text || msg.message_text || msg.message || msg.content}</p>
                        <span className={`block text-[9px] mt-1 text-right ${isSeller ? 'text-indigo-200' : 'text-slate-400'}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Send Input */}
            <form onSubmit={handleSendChatMessage} className="p-3 bg-slate-950/80 border-t border-white/10 flex items-center gap-2">
              <input
                type="text"
                placeholder="Type your message to user..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-indigo-400"
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || chatSending}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-md shadow-indigo-600/20"
              >
                {chatSending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span>Send</span>
                    <Send size={13} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Report Fake Payment Modal */}
      {disputeTx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-rose-500/30 rounded-[32px] p-6 md:p-8 max-w-lg w-full shadow-2xl relative animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setDisputeTx(null)}
              className="absolute top-5 right-5 p-2 text-slate-400 hover:text-white rounded-full bg-white/5 hover:bg-white/10 transition"
            >
              <X size={18} />
            </button>

            <div className="w-14 h-14 rounded-2xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400 mx-auto mb-4 shadow-lg shadow-rose-500/10">
              <ShieldAlert size={28} />
            </div>

            <h3 className="text-2xl font-black text-white text-center">
              Report Fake Payment
            </h3>
            <p className="text-xs text-slate-400 text-center mt-1 mb-5 leading-relaxed">
              If the recipient provided fake transaction proof or payment failed to arrive, raising this ticket will <strong className="text-rose-400">immediately freeze the coins</strong> from the user's wallet until admin review.
            </p>

            {disputeError && (
              <div className="p-3.5 mb-4 bg-rose-500/15 border border-rose-500/30 rounded-2xl text-xs text-rose-300 flex items-center gap-2">
                <AlertCircle size={16} className="shrink-0 text-rose-400" />
                <span>{disputeError}</span>
              </div>
            )}

            {disputeSuccess && (
              <div className="p-3.5 mb-4 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl text-xs text-emerald-300 flex items-center gap-2">
                <CheckCircle size={16} className="shrink-0 text-emerald-400" />
                <span>{disputeSuccess}</span>
              </div>
            )}

            <form onSubmit={handleSubmitDispute} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Recipient User ID / XOXO ID
                </label>
                <input
                  type="text"
                  value={disputeUserId}
                  onChange={(e) => setDisputeUserId(e.target.value)}
                  placeholder="e.g. 1029384 or UUID"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm font-semibold text-white placeholder-slate-500 focus:outline-none focus:border-rose-400"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Disputed Coins to Freeze
                </label>
                <input
                  type="number"
                  value={disputeCoins}
                  onChange={(e) => setDisputeCoins(e.target.value)}
                  placeholder="e.g. 10000"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm font-semibold text-white placeholder-slate-500 focus:outline-none focus:border-rose-400"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Dispute Reason
                </label>
                <select
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-2xl text-sm font-semibold text-white focus:outline-none focus:border-rose-400"
                >
                  <option value="Payment screenshot was fake / edited">Payment screenshot was fake / edited</option>
                  <option value="UTR / Transaction reference does not exist">UTR / Transaction reference does not exist</option>
                  <option value="Payment reversed / charged back">Payment reversed / charged back</option>
                  <option value="No money received in bank account">No money received in bank account</option>
                  <option value="Other">Other (custom)</option>
                </select>
              </div>

              {disputeReason === 'Other' && (
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                    Describe the issue
                  </label>
                  <textarea
                    rows={2}
                    value={disputeCustomReason}
                    onChange={(e) => setDisputeCustomReason(e.target.value)}
                    placeholder="Provide details about the fake payment..."
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-rose-400"
                    required
                  />
                </div>
              )}

              {/* Proof Screenshot / Statement File Upload */}
              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
                  Proof Screenshot / Statement File (Optional)
                </label>

                {!proofFile && !disputeProofUrl ? (
                  <label className="border-2 border-dashed border-white/15 hover:border-rose-400/50 bg-white/[0.02] hover:bg-white/[0.05] rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition group">
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleProofFileChange}
                      className="hidden"
                    />
                    <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center mb-2 group-hover:scale-110 transition">
                      <UploadCloud size={20} />
                    </div>
                    <p className="text-xs font-bold text-slate-200">
                      Click to upload screenshot or statement file
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Supports JPG, PNG, WEBP, PDF up to 10MB
                    </p>
                  </label>
                ) : (
                  <div className="p-3 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {proofPreview ? (
                        <img 
                          src={proofPreview} 
                          alt="Proof preview" 
                          className="w-12 h-12 rounded-xl object-cover border border-white/10 shrink-0" 
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center border border-white/10 shrink-0">
                          <FileText size={22} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-white truncate">
                          {proofFile ? proofFile.name : 'Proof Attached'}
                        </p>
                        <p className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1 mt-0.5">
                          {uploadingProof ? (
                            <>
                              <div className="w-2.5 h-2.5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                              <span>Uploading to server...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle size={11} />
                              <span>Uploaded & Attached</span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveProofFile}
                      className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition cursor-pointer shrink-0"
                      title="Remove file"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <div className="pt-1">
                  <span className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Or Enter Statement / Proof URL
                  </span>
                  <input
                    type="url"
                    value={disputeProofUrl}
                    onChange={(e) => setDisputeProofUrl(e.target.value)}
                    placeholder="https://... (or auto-filled when uploading above)"
                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-rose-400"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setDisputeTx(null)}
                  className="flex-1 py-3 bg-white/5 hover:bg-white/10 text-slate-300 font-bold rounded-2xl text-sm transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={disputeLoading}
                  className="flex-1 py-3 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold rounded-2xl text-sm transition shadow-lg shadow-rose-600/30 flex items-center justify-center gap-2"
                >
                  {disputeLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <ShieldAlert size={16} />
                      <span>Freeze & Submit</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Raised Disputes List Modal */}
      {showDisputesList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-white/10 rounded-[32px] p-6 md:p-8 max-w-2xl w-full shadow-2xl relative animate-in zoom-in-95 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center">
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white">Payment Disputes Tracker</h3>
                  <p className="text-xs text-slate-400">Tickets raised for fake or unreceived payments</p>
                </div>
              </div>
              <button
                onClick={() => setShowDisputesList(false)}
                className="p-2 text-slate-400 hover:text-white rounded-full bg-white/5 hover:bg-white/10 transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {sellerDisputes.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <ShieldAlert size={36} className="mx-auto mb-2 opacity-30 text-slate-400" />
                  <p className="text-sm font-semibold">No dispute tickets raised</p>
                  <p className="text-xs text-slate-500 mt-1">If any user gives you fake payment, report it from the Deposit History.</p>
                </div>
              ) : (
                sellerDisputes.map((d) => (
                  <div key={d.id} className="p-4 bg-white/5 border border-white/10 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white text-sm">
                          {d.user_name || 'User'}
                        </span>
                        {d.user_xoxo_id && (
                          <span className="px-2 py-0.5 rounded-md bg-white/10 text-amber-400 font-mono text-xs">
                            ID: {d.user_xoxo_id}
                          </span>
                        )}
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        d.status === 'frozen'
                          ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                          : d.status === 'resolved_refunded'
                          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                          : 'bg-slate-500/15 text-slate-300 border border-slate-500/30'
                      }`}>
                        {d.status === 'frozen' ? 'Coins Frozen (Pending Admin)' : d.status === 'resolved_refunded' ? 'Refunded to Seller' : d.status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Frozen Amount: <strong className="text-amber-400 font-bold">🪙 {Number(d.coins_amount).toLocaleString('en-IN')} Coins</strong></span>
                      <span>{d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</span>
                    </div>

                    <p className="text-xs text-slate-300 bg-slate-950/40 p-2.5 rounded-xl border border-white/5">
                      <strong className="text-slate-400">Reason:</strong> {d.reason}
                    </p>

                    {d.admin_notes && (
                      <p className="text-xs text-indigo-300 bg-indigo-500/10 p-2.5 rounded-xl border border-indigo-500/20">
                        <strong className="text-indigo-200">Admin Note:</strong> {d.admin_notes}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="pt-3 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setShowDisputesList(false)}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white font-bold rounded-xl text-xs transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seller Self-Recharge Slabs (Razorpay) Modal */}
      {showRechargeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-amber-500/30 rounded-[32px] shadow-2xl shadow-amber-500/10 w-full max-w-3xl p-6 md:p-8 animate-in zoom-in-95 max-h-[92vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center text-amber-400">
                  <Zap size={24} className="fill-amber-400" />
                </div>
                <div>
                  <h3 className="font-black text-white text-xl flex items-center gap-2">
                    <span>Recharge Seller Coins</span>
                    <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40">Wholesale Slabs</span>
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Select a wholesale package and pay via Razorpay to instantly add coins to your merchant wallet.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRechargeModal(false)}
                className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-white/5 transition cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Current Balance Banner */}
            <div className="mt-4 p-4 rounded-2xl bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-transparent border border-amber-400/20 flex items-center justify-between">
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Current Wallet Balance</span>
                <p className="text-xl font-black text-amber-400 mt-0.5">🪙 {Number(stats.current_balance).toLocaleString('en-IN')} Coins</p>
              </div>
              <div className="text-right">
                <span className="text-[11px] font-bold text-emerald-400 flex items-center gap-1 justify-end">
                  <ShieldCheck size={14} /> Instant Delivery
                </span>
                <p className="text-[11px] text-slate-400 mt-0.5">Auto-credited post payment</p>
              </div>
            </div>

            {/* Feedback Alerts */}
            {rechargeErrorMsg && (
              <div className="mt-4 p-3.5 bg-rose-500/15 border border-rose-500/30 rounded-2xl text-xs text-rose-300 font-semibold flex items-center gap-2">
                <AlertCircle size={16} className="shrink-0 text-rose-400" />
                <span>{rechargeErrorMsg}</span>
              </div>
            )}

            {rechargeSuccessMsg && (
              <div className="mt-4 p-3.5 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl text-xs text-emerald-300 font-semibold flex items-center gap-2">
                <CheckCircle2 size={16} className="shrink-0 text-emerald-400" />
                <span>{rechargeSuccessMsg}</span>
              </div>
            )}

            {/* Slabs Grid */}
            <div className="flex-1 overflow-y-auto my-4 pr-1 space-y-4">
              {loadingSlabs ? (
                <div className="py-16 text-center text-slate-400">
                  <RefreshCw size={28} className="animate-spin text-amber-400 mx-auto mb-3" />
                  <p className="text-sm">Loading wholesale coin packages...</p>
                </div>
              ) : coinSlabs.length === 0 ? (
                <div className="py-16 text-center text-slate-400">
                  <Coins size={36} className="opacity-30 mx-auto mb-3" />
                  <p className="text-sm">No coin packages currently configured by admin.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  {coinSlabs.map((slab) => {
                    const isSelected = selectedSlab?.id === slab.id;
                    const coinNum = Number(slab.coin_amount || 0);
                    const priceInr = Number(slab.price_inr || 0);
                    const discount = Number(slab.discount_pct || 0);

                    return (
                      <div
                        key={slab.id}
                        onClick={() => setSelectedSlab(slab)}
                        className={`p-4 rounded-2xl border-2 transition cursor-pointer relative flex flex-col justify-between ${
                          isSelected
                            ? 'bg-amber-500/15 border-amber-400 shadow-lg shadow-amber-500/20'
                            : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/[0.07]'
                        }`}
                      >
                        {discount > 0 && (
                          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white">
                            {discount}% OFF
                          </span>
                        )}

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isSelected ? 'bg-amber-400 text-slate-950' : 'bg-white/10 text-amber-400'}`}>
                              <Coins size={16} />
                            </div>
                            <span className="text-xs font-bold text-slate-400">Package #{slab.sort_order || slab.id}</span>
                          </div>

                          <div className="mt-1">
                            <p className="text-lg font-black text-white tracking-tight">
                              🪙 {coinNum.toLocaleString('en-IN')}
                            </p>
                            <p className="text-[11px] text-amber-300/80 font-semibold mt-0.5">
                              {coinNum >= 10000000 ? `${(coinNum / 10000000).toFixed(2)} Cr Coins` : coinNum >= 100000 ? `${(coinNum / 100000).toFixed(1)} Lakh Coins` : 'Wholesale Coins'}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
                          <div>
                            <span className="text-xs text-slate-400">Total Price:</span>
                            <div className="flex items-baseline gap-2">
                              <p className="text-base font-black text-emerald-400">
                                ₹{priceInr.toLocaleString('en-IN')}
                              </p>
                              <span className="text-xs font-bold text-blue-400">
                                (${Number(slab.price_usd || (priceInr / 94.0).toFixed(2))} USD)
                              </span>
                            </div>
                          </div>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center border ${isSelected ? 'bg-amber-400 border-amber-400 text-slate-950' : 'border-white/20'}`}>
                            {isSelected && <Check size={12} strokeWidth={3} />}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer / Checkout Action */}
            <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <ShieldCheck size={16} className="text-amber-400 shrink-0" />
                <span>Secured by <strong>Razorpay Gateway</strong> (UPI, Cards, NetBanking, GPay, PhonePe)</span>
              </div>

              <div className="flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowRechargeModal(false)}
                  className="px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 font-bold text-xs transition cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => handlePaySlab(selectedSlab)}
                  disabled={processingPayment || !selectedSlab}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-950 font-black text-sm shadow-xl shadow-amber-500/25 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
                >
                  {processingPayment ? (
                    <>
                      <RefreshCw size={16} className="animate-spin text-slate-950" />
                      <span>Processing Payment...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} />
                      <span>
                        Pay ₹{Number(selectedSlab?.price_inr || 0).toLocaleString('en-IN')} via Razorpay
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerDashboard;
